import { useMemo, useRef, useState } from "react";
import { func } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useActiveProjectStore, useTemplates } from "@hooks";
import { HtmlParser } from "@utils";
import { Grid, Link } from "@material-ui/core";
import { Button, PopMenu } from "@components";
import { FileAlt, XlsFile } from "@assets";
import { useBlankBorderStyle, useXlsBorderStyle } from "@styles";
import useStyle from "./TemplateSelector.style";
import classnames from "classnames";
import { darken } from "@material-ui/core/styles";

export const TemplateSelector = observer(({ onClick }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const { blankBorder } = useBlankBorderStyle();
  const { xlsBorder } = useXlsBorderStyle();
  const store = useActiveProjectStore();
  
  const { projectTypes } = useTemplates();
  
  const [detailsVisible, showDetails] = useState(false);

  const ref = useRef();

  const { template, hasXlsFile, xlsFileName } = store;

  const desc = useMemo(() => {
    if (template?.template) {
      const { MVP, MMP, MLP } = template.template;
      const sortedComplexities = [MVP, MMP, MLP].filter((x) => x);
      return sortedComplexities.map((x) => x.content).join("<br/><br/>");
    }

    return template?.content || "";
  }, [template, projectTypes, projectTypes.length]);
  
  const accentColor = useMemo(() => (
    template?.color || (template?.types?.[0] && projectTypes.find(p => p.id === template.types[0])?.backgroundColor)
  ), [template])

  return (
    <Grid item container className={classes.frame} onClick={onClick}>
      <Grid
        item
        container
        className={classnames(
          classes.templateContainer,
          template && !accentColor && classes.defaultAccent,
          !template && "border-dashed-2",
          !template && (hasXlsFile ? xlsBorder : blankBorder)
        )}
        style={{
          backgroundColor: accentColor,
          border: accentColor ? `2px dashed ${darken(accentColor, 0.3)}` : undefined
        }}
      >
        {template ? (
          template.thumb &&
          <img src={template.thumb} alt={`${template.title} graphic`} />
          // ) : (
          //   <TemplatePlaceholder />
          // )
        ) : hasXlsFile ? (
          <XlsFile style={{ fontSize: 60 }} />
        ) : (
          <FileAlt style={{ fontSize: 60 }} />
        )}
      </Grid>

      <Grid
        item
        container
        direction="column"
        wrap="nowrap"
        className={classes.templateDescription}
      >
        {template ? (
          <>
            <p className={classes.templateTitle}>{template.name}</p>
            <p className={classnames(classes.templateSubtitle)}>
              {HtmlParser(
                desc
                  .replace(/<[^>]*>/g, "")
                  .slice(0, 130)
                  .replace(/ \w+,?$/, "")
              )}
              {desc.length > 130 && (
                <Link ref={ref} onClick={() => showDetails(!detailsVisible)}>
                  {" ("}
                  {t("views.new_project.card.read_more")}
                  {"...)"}
                </Link>
              )}
            </p>
            <PopMenu
              show={detailsVisible}
              anchor={ref.current}
              onClickAway={() => showDetails(false)}
              className="p-6"
              style={{ width: 560 }}
            >
              <p>{HtmlParser(desc)}</p>
            </PopMenu>
          </>
        ) : (
          <>
            <p className={classes.templateTitle}>
              {hasXlsFile ? xlsFileName : t("views.templates.blank.title")}
            </p>
            {!hasXlsFile && (
              <p className={classes.templateSubtitle}>
                {t("views.templates.blank.desc_alt")}
              </p>
            )}
          </>
        )}
        <Button
          variant="outlined"
          color="primary"
          className="mt-4"
          size="small"
          onClick={onClick}
        >
          {t("views.new_project.change_template")}
        </Button>
      </Grid>
    </Grid>
  );
});

TemplateSelector.propTypes = {
  onClick: func,
};
